import { defineAthenaTenantConfig } from "@/configuration/ehr/athena/defaults";
import { referralDocumentTypeId } from "@/configuration/ehr/athena/policies";
import { AthenaReferringProviderMatchStrategy, PatientPrimaryDepartment } from "@/configuration/ehr/athena/types";
import { resolveTprCasePolicyPackageId } from "@/configuration/groups/tpr/case-policies";
import {
  TprBiltmoreAhiProviderNpi,
  TprBiltmoreArrowheadProviderNpi,
  TprBiltmoreCamelbackProviderNpi,
  TprBiltmoreCasaGrandeProviderNpi,
  TprBiltmoreGoodyearProviderNpi,
  isTprBiltmoreTelehealthProviderNpi,
  TprBiltmorePeoriaProviderNpi,
  TprBiltmoreScottsdaleProviderNpi,
  TprBiltmoreWestProviderNpi,
  type TprBiltmoreNetworkProviderNpi,
} from "@/configuration/groups/tpr/configuration";
import { tprAthenaCoverageWrite } from "@/configuration/groups/tpr/workflows/coverage-write-review";
import { getGroupConfigSubdomain } from "@/configuration/paths";
import { ConfigSubdomain } from "@/configuration/tenant-ids";
import { logAndThrow } from "@/src/lib/logging/logger";
import { AppointmentType } from "@/src/models/appointment";
import type { Group } from "@/src/models/group";

export const TPR_BILTMORE_DEPARTMENT_ID = "648";
export const TPR_BILTMORE_AHI_DEPARTMENT_ID = "625";
export const TPR_BILTMORE_ARROWHEAD_DEPARTMENT_ID = "381";
export const TPR_BILTMORE_GOODYEAR_DEPARTMENT_ID = "622";
export const TPR_BILTMORE_WEST_DEPARTMENT_ID = "683";
export const TPR_BILTMORE_VERRADO_DEPARTMENT_ID = "747";
export const TPR_BILTMORE_SURPRISE_DEPARTMENT_ID = "708";
export const TPR_BILTMORE_PEORIA_DEPARTMENT_ID = "624";
export const TPR_BILTMORE_SCOTTSDALE_DEPARTMENT_ID = "594";
export const TPR_BILTMORE_CENTRAL_DEPARTMENT_ID = "576";
export const TPR_BILTMORE_CASA_GRANDE_DEPARTMENT_ID = "523";
export const TPR_BILTMORE_TERRACE_DEPARTMENT_ID = "678";
export const TPR_BILTMORE_WICKENBURG_DEPARTMENT_ID = "752";
export const TPR_BILTMORE_KIERLAND_DEPARTMENT_ID = "772";
export const TPR_ARIZONA_HEART_HOSPITAL_IP_DEPARTMENT_ID = "253";
export const TPR_ARIZONA_HEART_HOSPITAL_OP_DEPARTMENT_ID = "255";
export const TPR_ATHENA_SELF_PAY_INSURANCE_PACKAGE_ID = "0";

/** Departments explicitly authorized for read-only related-location appointment history and confirmation. */
export const TPR_RELATED_APPOINTMENT_HISTORY_DEPARTMENT_IDS = [
  TPR_BILTMORE_AHI_DEPARTMENT_ID,
  TPR_BILTMORE_ARROWHEAD_DEPARTMENT_ID,
  TPR_BILTMORE_GOODYEAR_DEPARTMENT_ID,
  TPR_BILTMORE_WEST_DEPARTMENT_ID,
  TPR_BILTMORE_VERRADO_DEPARTMENT_ID,
  TPR_BILTMORE_SURPRISE_DEPARTMENT_ID,
  TPR_BILTMORE_PEORIA_DEPARTMENT_ID,
  TPR_BILTMORE_SCOTTSDALE_DEPARTMENT_ID,
  TPR_BILTMORE_CENTRAL_DEPARTMENT_ID,
  TPR_BILTMORE_CASA_GRANDE_DEPARTMENT_ID,
  TPR_BILTMORE_TERRACE_DEPARTMENT_ID,
  TPR_BILTMORE_WICKENBURG_DEPARTMENT_ID,
  TPR_BILTMORE_KIERLAND_DEPARTMENT_ID,
] as const;

/** Hospital departments that may be read only for appointment confirmation. */
export const TPR_ADDITIONAL_APPOINTMENT_CONFIRMATION_DEPARTMENT_IDS = [
  TPR_ARIZONA_HEART_HOSPITAL_IP_DEPARTMENT_ID,
  TPR_ARIZONA_HEART_HOSPITAL_OP_DEPARTMENT_ID,
] as const;

// Existing Athena practice-600 department catalog. Analytics labels intentionally stay short;
// caller-facing names preserve each office's verified public branding and preposition.
const TPR_DEPARTMENTS: Readonly<Record<string, { analyticsName: string; callerFacingName: string }>> = {
  [TPR_BILTMORE_DEPARTMENT_ID]: {
    analyticsName: "Camelback",
    callerFacingName: "Biltmore Cardiology - Camelback",
  },
  [TPR_BILTMORE_AHI_DEPARTMENT_ID]: {
    analyticsName: "AHI",
    callerFacingName: "Biltmore Cardiology at Arizona Heart",
  },
  [TPR_BILTMORE_ARROWHEAD_DEPARTMENT_ID]: {
    analyticsName: "Arrowhead",
    callerFacingName: "Biltmore Cardiology at Arrowhead Campus",
  },
  [TPR_BILTMORE_GOODYEAR_DEPARTMENT_ID]: {
    analyticsName: "Goodyear",
    callerFacingName: "Biltmore Cardiology - Goodyear",
  },
  [TPR_BILTMORE_WEST_DEPARTMENT_ID]: {
    analyticsName: "West",
    callerFacingName: "Biltmore Cardiology at West Campus",
  },
  [TPR_BILTMORE_VERRADO_DEPARTMENT_ID]: {
    analyticsName: "Verrado",
    callerFacingName: "Biltmore Cardiology - Verrado Way",
  },
  [TPR_BILTMORE_SURPRISE_DEPARTMENT_ID]: {
    analyticsName: "Surprise",
    callerFacingName: "Biltmore Cardiology - Surprise",
  },
  [TPR_BILTMORE_PEORIA_DEPARTMENT_ID]: {
    analyticsName: "Peoria",
    callerFacingName: "Biltmore Cardiology at Peoria",
  },
  [TPR_BILTMORE_SCOTTSDALE_DEPARTMENT_ID]: {
    analyticsName: "Scottsdale",
    callerFacingName: "Biltmore Cardiology at Scottsdale Campus",
  },
  [TPR_BILTMORE_CENTRAL_DEPARTMENT_ID]: {
    analyticsName: "Central",
    callerFacingName: "Biltmore Cardiology at Central Campus",
  },
  [TPR_BILTMORE_CASA_GRANDE_DEPARTMENT_ID]: {
    analyticsName: "Casa Grande",
    callerFacingName: "Biltmore Cardiology - Casa Grande",
  },
  // Not yet in the ops physician-roster sheet (physician-roster.ts) — valid receiving locations,
  // but diagnosis-match provider selection won't find anyone practicing here until ops supplies
  // roster data for these sites. Terrace is the only Biltmore site not branded "Cardio" in Athena;
  // it also treats neurology, so receiving-provider-location.ts flags it for a cardiology check.
  [TPR_BILTMORE_TERRACE_DEPARTMENT_ID]: {
    analyticsName: "Terrace",
    callerFacingName: "Biltmore Terrace",
  },
  [TPR_BILTMORE_WICKENBURG_DEPARTMENT_ID]: {
    analyticsName: "Wickenburg",
    callerFacingName: "Biltmore Cardiology - Wickenburg",
  },
  [TPR_BILTMORE_KIERLAND_DEPARTMENT_ID]: {
    analyticsName: "Kierland",
    callerFacingName: "Biltmore Cardiology - Kierland",
  },
};

/** Non-office departments that Voice may name only when reporting an existing appointment. */
const TPR_READ_ONLY_APPOINTMENT_DEPARTMENTS: Readonly<Record<string, { callerFacingName: string }>> = {
  [TPR_ARIZONA_HEART_HOSPITAL_IP_DEPARTMENT_ID]: {
    callerFacingName: "Arizona Heart Hospital",
  },
  [TPR_ARIZONA_HEART_HOSPITAL_OP_DEPARTMENT_ID]: {
    callerFacingName: "Arizona Heart Hospital",
  },
};

export const TPR_DEPARTMENT_NAMES: Readonly<Record<string, string>> = Object.fromEntries(
  Object.entries(TPR_DEPARTMENTS).map(([departmentId, { analyticsName }]) => [departmentId, analyticsName])
);

export function getTprDepartmentIds(): string[] {
  return Object.keys(TPR_DEPARTMENT_NAMES);
}

export function getTprDepartmentName(departmentId: string): string | null {
  return TPR_DEPARTMENT_NAMES[departmentId] ?? null;
}

export function getTprDepartmentCallerFacingName(departmentId: string): string | null {
  return (
    TPR_DEPARTMENTS[departmentId]?.callerFacingName ??
    TPR_READ_ONLY_APPOINTMENT_DEPARTMENTS[departmentId]?.callerFacingName ??
    null
  );
}

function normalizeTprLocationName(locationName: string): string {
  return ` ${locationName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim()} `;
}

export function getTprDepartmentIdFromLocationName(locationName: string | null | undefined): string | null {
  if (!locationName) return null;

  const normalizedLocationName = normalizeTprLocationName(locationName);
  const matches = Object.entries(TPR_DEPARTMENT_NAMES).filter(([, departmentName]) => {
    const normalizedDepartmentName = departmentName
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, " ")
      .trim();

    return normalizedLocationName.includes(` ${normalizedDepartmentName} `);
  });

  return matches.length === 1 ? (matches[0]?.[0] ?? null) : null;
}

/**
 * Same site-name lookup, but only for a location that is *itself* branded Biltmore.
 *
 * `getTprDepartmentIdFromLocationName` matches a bare site name anywhere in the string. That is
 * safe for our own receiving-location roster but over-matches outside practices whose names came
 * off an inbound fax ("Optum Internal Medicine - Sun City West"), so require the brand token first.
 */
export function getTprBiltmoreDepartmentIdFromLocationName(locationName: string | null | undefined): string | null {
  if (!locationName || !normalizeTprLocationName(locationName).includes(" biltmore ")) return null;

  return getTprDepartmentIdFromLocationName(locationName);
}

// Request for Additional Information
export const TPR_MISSING_INFO_DOCUMENT_TYPE_ID = 390522;
// Request for Authorization - Referral
export const TPR_REFERRAL_AUTH_DOCUMENT_TYPE_ID = 395521;
const TPR_REFERRAL_SOURCE_ID = "85";

const TPR_BILTMORE_APPOINTMENT_TYPE_IDS: Partial<Record<AppointmentType, number>> = {
  [AppointmentType.INITIAL_CONSULTATION]: 3624,
  [AppointmentType.NEW_PATIENT_CHECKIN]: 3624,
  [AppointmentType.FOLLOW_UP]: 3116,
  [AppointmentType.EXISTING_PATIENT_CHECKIN]: 766,
  [AppointmentType.TELEHEALTH]: 3673,
};

enum TprBiltmoreOpenAppointmentTypeId {
  ANY_10 = 2959,
  ANY_15 = 2962,
  ANY_20 = 2966,
  ANY_30 = 2968,
  ANY_30_OFFICE_USE_ONLY = 2969,
  ESTABLISHED_PATIENT_10 = 210,
  ESTABLISHED_PATIENT_15 = 766,
  ESTABLISHED_PATIENT_20 = 211,
  ESTABLISHED_PATIENT_30 = 212,
  HOSPITAL_FOLLOW_UP_10 = 3005,
  HOSPITAL_FOLLOW_UP_15 = 3116,
  HOSPITAL_FOLLOW_UP_20 = 3006,
  HOSPITAL_FOLLOW_UP_30 = 3536,
  NEW_PATIENT_10 = 166,
  NEW_PATIENT_15 = 3624,
  NEW_PATIENT_30 = 3625,
  NEW_PATIENT_40 = 3626,
  TELEMED_10 = 3672,
  TELEMED_15 = 3673,
}

const TPR_BILTMORE_ANY_OPEN_APPOINTMENT_TYPE_IDS: ReadonlySet<number> = new Set([
  TprBiltmoreOpenAppointmentTypeId.ANY_10,
  TprBiltmoreOpenAppointmentTypeId.ANY_15,
  TprBiltmoreOpenAppointmentTypeId.ANY_20,
  TprBiltmoreOpenAppointmentTypeId.ANY_30,
]);

const TPR_BILTMORE_BOOKED_APPOINTMENT_TYPES: Record<number, AppointmentType> = {
  [TprBiltmoreOpenAppointmentTypeId.ANY_10]: AppointmentType.UNKNOWN,
  [TprBiltmoreOpenAppointmentTypeId.ANY_15]: AppointmentType.UNKNOWN,
  [TprBiltmoreOpenAppointmentTypeId.ANY_20]: AppointmentType.UNKNOWN,
  [TprBiltmoreOpenAppointmentTypeId.ANY_30]: AppointmentType.UNKNOWN,
  [TprBiltmoreOpenAppointmentTypeId.ANY_30_OFFICE_USE_ONLY]: AppointmentType.UNKNOWN,
  [TprBiltmoreOpenAppointmentTypeId.ESTABLISHED_PATIENT_10]: AppointmentType.EXISTING_PATIENT_CHECKIN,
  [TprBiltmoreOpenAppointmentTypeId.ESTABLISHED_PATIENT_15]: AppointmentType.EXISTING_PATIENT_CHECKIN,
  [TprBiltmoreOpenAppointmentTypeId.ESTABLISHED_PATIENT_20]: AppointmentType.EXISTING_PATIENT_CHECKIN,
  [TprBiltmoreOpenAppointmentTypeId.ESTABLISHED_PATIENT_30]: AppointmentType.EXISTING_PATIENT_CHECKIN,
  [TprBiltmoreOpenAppointmentTypeId.HOSPITAL_FOLLOW_UP_10]: AppointmentType.FOLLOW_UP,
  [TprBiltmoreOpenAppointmentTypeId.HOSPITAL_FOLLOW_UP_15]: AppointmentType.FOLLOW_UP,
  [TprBiltmoreOpenAppointmentTypeId.HOSPITAL_FOLLOW_UP_20]: AppointmentType.FOLLOW_UP,
  [TprBiltmoreOpenAppointmentTypeId.HOSPITAL_FOLLOW_UP_30]: AppointmentType.FOLLOW_UP,
  [TprBiltmoreOpenAppointmentTypeId.NEW_PATIENT_10]: AppointmentType.NEW_PATIENT_CHECKIN,
  [TprBiltmoreOpenAppointmentTypeId.NEW_PATIENT_15]: AppointmentType.NEW_PATIENT_CHECKIN,
  [TprBiltmoreOpenAppointmentTypeId.NEW_PATIENT_30]: AppointmentType.NEW_PATIENT_CHECKIN,
  [TprBiltmoreOpenAppointmentTypeId.NEW_PATIENT_40]: AppointmentType.NEW_PATIENT_CHECKIN,
  [TprBiltmoreOpenAppointmentTypeId.TELEMED_10]: AppointmentType.TELEHEALTH,
  [TprBiltmoreOpenAppointmentTypeId.TELEMED_15]: AppointmentType.TELEHEALTH,
};

export function getTprBiltmoreAppointmentTypeFromBookedEhrId(
  _providerEhrId: string,
  appointmentTypeId: number
): AppointmentType | null {
  // Unlisted booked types fall back to Athena's label classifier. Explicit catch-all types above
  // remain UNKNOWN, while hospital labels such as "Procedure 60" stay read-only procedures.
  return TPR_BILTMORE_BOOKED_APPOINTMENT_TYPES[appointmentTypeId] ?? null;
}

// Athena's generic "Follow Up" type is marked "Do Not Use" and is intentionally not mapped.

type TprBiltmoreProviderOpenAppointmentTypes = {
  any: TprBiltmoreOpenAppointmentTypeId | null;
  newPatient: readonly TprBiltmoreOpenAppointmentTypeId[];
  establishedPatient: readonly TprBiltmoreOpenAppointmentTypeId[];
  hospitalFollowUp: readonly TprBiltmoreOpenAppointmentTypeId[];
  telehealth: readonly TprBiltmoreOpenAppointmentTypeId[];
};

const TPR_BILTMORE_UNCONFIRMED_OPEN_APPOINTMENT_TYPES: TprBiltmoreProviderOpenAppointmentTypes = {
  any: null,
  newPatient: [],
  establishedPatient: [],
  hospitalFollowUp: [],
  telehealth: [],
};

// TPR practitioners are configured and resolved by NPI; their Athena provider IDs are populated dynamically.
// This matrix mirrors the Camelback/Biltmore Athena scheduling-slot guide. Generic Any types are
// provider-specific and are offered only alongside a configured visit type. Empty type lists remain unschedulable.
const TPR_BILTMORE_PROVIDER_OPEN_APPOINTMENT_TYPES: Readonly<
  Record<TprBiltmoreNetworkProviderNpi, TprBiltmoreProviderOpenAppointmentTypes>
> = {
  // Athena practice 600 / department 648 provider-specific appointment types, verified 2026-09-24.
  [TprBiltmoreCamelbackProviderNpi.AMILEE_NING]: {
    // Generic Any 30 slots book as the first specific type, so list 30-minute types first.
    any: TprBiltmoreOpenAppointmentTypeId.ANY_30,
    newPatient: [TprBiltmoreOpenAppointmentTypeId.NEW_PATIENT_30, TprBiltmoreOpenAppointmentTypeId.NEW_PATIENT_15],
    establishedPatient: [
      TprBiltmoreOpenAppointmentTypeId.ESTABLISHED_PATIENT_30,
      TprBiltmoreOpenAppointmentTypeId.ESTABLISHED_PATIENT_15,
    ],
    hospitalFollowUp: [
      TprBiltmoreOpenAppointmentTypeId.HOSPITAL_FOLLOW_UP_30,
      TprBiltmoreOpenAppointmentTypeId.HOSPITAL_FOLLOW_UP_15,
    ],
    telehealth: [],
  },
  [TprBiltmoreCamelbackProviderNpi.KRISTIN_FRANCO]: {
    any: null,
    // New patients in the Loli/Franco care team must be scheduled by Camelback staff.
    newPatient: [],
    establishedPatient: [
      TprBiltmoreOpenAppointmentTypeId.ESTABLISHED_PATIENT_15,
      TprBiltmoreOpenAppointmentTypeId.ESTABLISHED_PATIENT_30,
    ],
    hospitalFollowUp: [
      TprBiltmoreOpenAppointmentTypeId.HOSPITAL_FOLLOW_UP_15,
      TprBiltmoreOpenAppointmentTypeId.HOSPITAL_FOLLOW_UP_30,
    ],
    telehealth: [],
  },
  [TprBiltmoreCamelbackProviderNpi.RICO_SEGUN]: {
    any: TprBiltmoreOpenAppointmentTypeId.ANY_15,
    newPatient: [],
    establishedPatient: [
      TprBiltmoreOpenAppointmentTypeId.ESTABLISHED_PATIENT_15,
      TprBiltmoreOpenAppointmentTypeId.ESTABLISHED_PATIENT_20,
    ],
    hospitalFollowUp: [],
    telehealth: [],
  },
  // Diana Thayer
  [TprBiltmoreCamelbackProviderNpi.DIANA_THAYER]: {
    any: TprBiltmoreOpenAppointmentTypeId.ANY_15,
    newPatient: [],
    establishedPatient: [TprBiltmoreOpenAppointmentTypeId.ESTABLISHED_PATIENT_15],
    hospitalFollowUp: [],
    telehealth: [],
  },
  // Danielle Sturm (Lorde)
  [TprBiltmoreCamelbackProviderNpi.DANIELLE_STURM]: {
    any: TprBiltmoreOpenAppointmentTypeId.ANY_15,
    newPatient: [TprBiltmoreOpenAppointmentTypeId.NEW_PATIENT_15],
    establishedPatient: [TprBiltmoreOpenAppointmentTypeId.ESTABLISHED_PATIENT_15],
    hospitalFollowUp: [TprBiltmoreOpenAppointmentTypeId.HOSPITAL_FOLLOW_UP_15],
    telehealth: [TprBiltmoreOpenAppointmentTypeId.TELEMED_15],
  },
  // Lorraine Homes
  [TprBiltmoreCamelbackProviderNpi.LORRAINE_HOMES]: {
    any: TprBiltmoreOpenAppointmentTypeId.ANY_15,
    newPatient: [TprBiltmoreOpenAppointmentTypeId.NEW_PATIENT_15],
    establishedPatient: [TprBiltmoreOpenAppointmentTypeId.ESTABLISHED_PATIENT_15],
    hospitalFollowUp: [TprBiltmoreOpenAppointmentTypeId.HOSPITAL_FOLLOW_UP_15],
    telehealth: [TprBiltmoreOpenAppointmentTypeId.TELEMED_15],
  },
  // Chris Lichtenwalter
  [TprBiltmoreCamelbackProviderNpi.CHRIS_LICHTENWALTER]: {
    any: TprBiltmoreOpenAppointmentTypeId.ANY_10,
    newPatient: [TprBiltmoreOpenAppointmentTypeId.NEW_PATIENT_10],
    establishedPatient: [TprBiltmoreOpenAppointmentTypeId.ESTABLISHED_PATIENT_10],
    hospitalFollowUp: [],
    telehealth: [TprBiltmoreOpenAppointmentTypeId.TELEMED_10],
  },
  // Marium Muzaffar
  [TprBiltmoreCamelbackProviderNpi.MARIUM_MUZAFFAR]: {
    any: TprBiltmoreOpenAppointmentTypeId.ANY_15,
    newPatient: [TprBiltmoreOpenAppointmentTypeId.NEW_PATIENT_15],
    establishedPatient: [TprBiltmoreOpenAppointmentTypeId.ESTABLISHED_PATIENT_15],
    hospitalFollowUp: [],
    telehealth: [TprBiltmoreOpenAppointmentTypeId.TELEMED_15],
  },
  // Peter Maki
  [TprBiltmoreCamelbackProviderNpi.PETER_MAKI]: {
    any: null,
    newPatient: [TprBiltmoreOpenAppointmentTypeId.NEW_PATIENT_15],
    establishedPatient: [TprBiltmoreOpenAppointmentTypeId.ESTABLISHED_PATIENT_15],
    hospitalFollowUp: [TprBiltmoreOpenAppointmentTypeId.HOSPITAL_FOLLOW_UP_15],
    telehealth: [TprBiltmoreOpenAppointmentTypeId.TELEMED_15],
  },
  // Timothy Byrne
  [TprBiltmoreCamelbackProviderNpi.TIMOTHY_BYRNE]: {
    any: null,
    newPatient: [TprBiltmoreOpenAppointmentTypeId.NEW_PATIENT_15],
    establishedPatient: [TprBiltmoreOpenAppointmentTypeId.ESTABLISHED_PATIENT_15],
    hospitalFollowUp: [TprBiltmoreOpenAppointmentTypeId.HOSPITAL_FOLLOW_UP_15],
    telehealth: [],
  },
  // Nickalaus Gramze
  [TprBiltmoreCamelbackProviderNpi.NICKALAUS_GRAMZE]: {
    // Any 20 is scoped to 20-minute visits; new patients require NP 40.
    any: null,
    newPatient: [TprBiltmoreOpenAppointmentTypeId.NEW_PATIENT_40],
    establishedPatient: [
      TprBiltmoreOpenAppointmentTypeId.ESTABLISHED_PATIENT_20,
      TprBiltmoreOpenAppointmentTypeId.ANY_20,
    ],
    hospitalFollowUp: [TprBiltmoreOpenAppointmentTypeId.HOSPITAL_FOLLOW_UP_20, TprBiltmoreOpenAppointmentTypeId.ANY_20],
    telehealth: [TprBiltmoreOpenAppointmentTypeId.TELEMED_15],
  },
  // Akram Ibrahim
  [TprBiltmoreCamelbackProviderNpi.AKRAM_IBRAHIM]: {
    any: TprBiltmoreOpenAppointmentTypeId.ANY_10,
    newPatient: [TprBiltmoreOpenAppointmentTypeId.NEW_PATIENT_10],
    establishedPatient: [TprBiltmoreOpenAppointmentTypeId.ESTABLISHED_PATIENT_10],
    hospitalFollowUp: [TprBiltmoreOpenAppointmentTypeId.HOSPITAL_FOLLOW_UP_10],
    telehealth: [],
  },
  // Lawrence Kline
  [TprBiltmoreCamelbackProviderNpi.LAWRENCE_KLINE]: {
    any: TprBiltmoreOpenAppointmentTypeId.ANY_15,
    newPatient: [TprBiltmoreOpenAppointmentTypeId.NEW_PATIENT_15],
    establishedPatient: [TprBiltmoreOpenAppointmentTypeId.ESTABLISHED_PATIENT_15],
    hospitalFollowUp: [],
    telehealth: [TprBiltmoreOpenAppointmentTypeId.TELEMED_15],
  },
  // Neal Klein
  [TprBiltmoreCamelbackProviderNpi.NEAL_KLEIN]: {
    any: TprBiltmoreOpenAppointmentTypeId.ANY_15,
    newPatient: [TprBiltmoreOpenAppointmentTypeId.NEW_PATIENT_15],
    establishedPatient: [TprBiltmoreOpenAppointmentTypeId.ESTABLISHED_PATIENT_15],
    hospitalFollowUp: [],
    telehealth: [TprBiltmoreOpenAppointmentTypeId.TELEMED_15],
  },
  // Kristen Jensen
  [TprBiltmoreCamelbackProviderNpi.KRISTEN_JENSEN]: {
    any: TprBiltmoreOpenAppointmentTypeId.ANY_15,
    newPatient: [TprBiltmoreOpenAppointmentTypeId.NEW_PATIENT_15],
    establishedPatient: [TprBiltmoreOpenAppointmentTypeId.ESTABLISHED_PATIENT_15],
    hospitalFollowUp: [TprBiltmoreOpenAppointmentTypeId.HOSPITAL_FOLLOW_UP_15],
    telehealth: [TprBiltmoreOpenAppointmentTypeId.TELEMED_15],
  },
  // Jody Eckhardt
  [TprBiltmoreCamelbackProviderNpi.JODY_ECKHARDT]: {
    any: null,
    newPatient: [TprBiltmoreOpenAppointmentTypeId.NEW_PATIENT_30],
    establishedPatient: [TprBiltmoreOpenAppointmentTypeId.ESTABLISHED_PATIENT_30],
    hospitalFollowUp: [TprBiltmoreOpenAppointmentTypeId.HOSPITAL_FOLLOW_UP_30],
    telehealth: [TprBiltmoreOpenAppointmentTypeId.TELEMED_15],
  },
  // Kevin Murphy
  [TprBiltmoreCamelbackProviderNpi.KEVIN_MURPHY]: {
    any: TprBiltmoreOpenAppointmentTypeId.ANY_30,
    newPatient: [TprBiltmoreOpenAppointmentTypeId.NEW_PATIENT_30],
    establishedPatient: [TprBiltmoreOpenAppointmentTypeId.ESTABLISHED_PATIENT_30],
    hospitalFollowUp: [TprBiltmoreOpenAppointmentTypeId.HOSPITAL_FOLLOW_UP_30],
    telehealth: [TprBiltmoreOpenAppointmentTypeId.TELEMED_15],
  },
  // Elizabeth Steffens
  [TprBiltmoreCamelbackProviderNpi.ELIZABETH_STEFFENS]: {
    any: null,
    newPatient: [],
    establishedPatient: [TprBiltmoreOpenAppointmentTypeId.ESTABLISHED_PATIENT_30],
    hospitalFollowUp: [],
    telehealth: [],
  },
  [TprBiltmoreCamelbackProviderNpi.RENZO_CATALDO]: {
    // Athena marks Office Use Only as non-generic; retain this type when booking.
    any: null,
    newPatient: [TprBiltmoreOpenAppointmentTypeId.ANY_30_OFFICE_USE_ONLY],
    establishedPatient: [TprBiltmoreOpenAppointmentTypeId.ANY_30_OFFICE_USE_ONLY],
    hospitalFollowUp: [TprBiltmoreOpenAppointmentTypeId.ANY_30_OFFICE_USE_ONLY],
    telehealth: [],
  },
  [TprBiltmoreCamelbackProviderNpi.AKIL_LOLI]: TPR_BILTMORE_UNCONFIRMED_OPEN_APPOINTMENT_TYPES,
  [TprBiltmoreCamelbackProviderNpi.MARWAN_BAHU]: TPR_BILTMORE_UNCONFIRMED_OPEN_APPOINTMENT_TYPES,
  [TprBiltmoreCamelbackProviderNpi.ARCOT_PREMKUMAR]: TPR_BILTMORE_UNCONFIRMED_OPEN_APPOINTMENT_TYPES,
  [TprBiltmoreCamelbackProviderNpi.MELINDA_NEWMAN]: TPR_BILTMORE_UNCONFIRMED_OPEN_APPOINTMENT_TYPES,
  [TprBiltmoreCamelbackProviderNpi.JENNA_GONZALES]: TPR_BILTMORE_UNCONFIRMED_OPEN_APPOINTMENT_TYPES,
  [TprBiltmoreAhiProviderNpi.MICHELLE_MIX]: TPR_BILTMORE_UNCONFIRMED_OPEN_APPOINTMENT_TYPES,
  [TprBiltmoreAhiProviderNpi.AYMAN_JAMAL]: TPR_BILTMORE_UNCONFIRMED_OPEN_APPOINTMENT_TYPES,
  [TprBiltmoreCasaGrandeProviderNpi.JASON_COOL]: TPR_BILTMORE_UNCONFIRMED_OPEN_APPOINTMENT_TYPES,
  [TprBiltmoreWestProviderNpi.JAMES_KIM]: TPR_BILTMORE_UNCONFIRMED_OPEN_APPOINTMENT_TYPES,
  [TprBiltmoreWestProviderNpi.RICHARD_PHAM]: TPR_BILTMORE_UNCONFIRMED_OPEN_APPOINTMENT_TYPES,
  [TprBiltmoreWestProviderNpi.RAJ_SHAH]: TPR_BILTMORE_UNCONFIRMED_OPEN_APPOINTMENT_TYPES,
  [TprBiltmoreGoodyearProviderNpi.SHARON_SADEGHINIA]: TPR_BILTMORE_UNCONFIRMED_OPEN_APPOINTMENT_TYPES,
  [TprBiltmoreArrowheadProviderNpi.RAHOOL_KARNIK]: TPR_BILTMORE_UNCONFIRMED_OPEN_APPOINTMENT_TYPES,
  [TprBiltmoreArrowheadProviderNpi.KRISTINA_SKINNER]: TPR_BILTMORE_UNCONFIRMED_OPEN_APPOINTMENT_TYPES,
  [TprBiltmoreArrowheadProviderNpi.DAVID_PRUTZMAN]: TPR_BILTMORE_UNCONFIRMED_OPEN_APPOINTMENT_TYPES,
  [TprBiltmoreArrowheadProviderNpi.MAEDEH_KHOLGHI]: TPR_BILTMORE_UNCONFIRMED_OPEN_APPOINTMENT_TYPES,
  [TprBiltmoreScottsdaleProviderNpi.ASHISH_GUPTA]: TPR_BILTMORE_UNCONFIRMED_OPEN_APPOINTMENT_TYPES,
  [TprBiltmoreScottsdaleProviderNpi.WILLIAM_JAFFE]: TPR_BILTMORE_UNCONFIRMED_OPEN_APPOINTMENT_TYPES,
  [TprBiltmorePeoriaProviderNpi.SAIFULDEEN_AL_QAISI]: TPR_BILTMORE_UNCONFIRMED_OPEN_APPOINTMENT_TYPES,
};

function isTprBiltmoreNetworkProviderNpi(providerNpi: string): providerNpi is TprBiltmoreNetworkProviderNpi {
  return Object.hasOwn(TPR_BILTMORE_PROVIDER_OPEN_APPOINTMENT_TYPES, providerNpi);
}

export function isTprBiltmoreGroup(group: Pick<Group, "subdomain">): boolean {
  return getGroupConfigSubdomain(group) === ConfigSubdomain.TPR;
}

export function getTprBiltmoreAppointmentTypeId(
  group: Pick<Group, "subdomain">,
  appointmentType: AppointmentType
): number | null {
  if (!isTprBiltmoreGroup(group)) return null;

  const appointmentTypeId = TPR_BILTMORE_APPOINTMENT_TYPE_IDS[appointmentType];

  if (appointmentTypeId === undefined) {
    logAndThrow("No TPR Biltmore appointment type configured", { appointmentType });
  }

  return appointmentTypeId;
}

export function getTprBiltmoreOpenAppointmentTypeIds(
  group: Pick<Group, "subdomain">,
  providerNpi: string | null | undefined,
  appointmentType: AppointmentType
): readonly number[] | null {
  if (!isTprBiltmoreGroup(group)) return null;

  if (!providerNpi) {
    logAndThrow("Provider NPI is required to resolve TPR Biltmore open appointment types", { appointmentType });
  }

  if (!isTprBiltmoreNetworkProviderNpi(providerNpi)) {
    logAndThrow("No TPR Biltmore open appointment types configured for provider", { providerNpi, appointmentType });
  }

  const mapping = TPR_BILTMORE_PROVIDER_OPEN_APPOINTMENT_TYPES[providerNpi];

  // The Athena guide records providers with Telemed slots, but TPR's Voice policy currently allows
  // virtual scheduling only with Chris Lichtenwalter. Enforce that policy at availability and booking.
  if (appointmentType === AppointmentType.TELEHEALTH && !isTprBiltmoreTelehealthProviderNpi(providerNpi)) {
    return [];
  }

  let useCaseTypeIds: readonly TprBiltmoreOpenAppointmentTypeId[];

  switch (appointmentType) {
    case AppointmentType.INITIAL_CONSULTATION:
    case AppointmentType.NEW_PATIENT_CHECKIN:
      useCaseTypeIds = mapping.newPatient;
      break;
    case AppointmentType.FOLLOW_UP:
      useCaseTypeIds = mapping.hospitalFollowUp;
      break;
    case AppointmentType.EXISTING_PATIENT_CHECKIN:
      useCaseTypeIds = mapping.establishedPatient;
      break;
    case AppointmentType.TELEHEALTH:
      useCaseTypeIds = mapping.telehealth;
      break;
    default:
      logAndThrow("No TPR Biltmore open appointment types configured for appointment type", {
        providerNpi,
        appointmentType,
      });
  }

  return useCaseTypeIds.length > 0 && mapping.any !== null ? [...useCaseTypeIds, mapping.any] : useCaseTypeIds;
}

export function getTprBiltmoreBookedTypeIdFromOpenTypeId(
  group: Pick<Group, "subdomain">,
  providerNpi: string | null | undefined,
  openAppointmentTypeId: string,
  internalAppointmentType: AppointmentType | null
): number | null {
  if (!isTprBiltmoreGroup(group)) return null;

  if (!providerNpi) {
    logAndThrow("No TPR Biltmore provider NPI configured for open appointment type", {
      openAppointmentTypeId,
      internalAppointmentType,
    });
  }

  if (!internalAppointmentType) {
    logAndThrow("No TPR Biltmore booking type configured for open appointment type", {
      providerNpi,
      openAppointmentTypeId,
    });
  }

  const eligibleOpenAppointmentTypeIds = getTprBiltmoreOpenAppointmentTypeIds(
    group,
    providerNpi,
    internalAppointmentType
  );
  const openAppointmentTypeIdNumber = Number(openAppointmentTypeId);

  if (!eligibleOpenAppointmentTypeIds?.includes(openAppointmentTypeIdNumber)) {
    logAndThrow("Open appointment type is not configured for TPR Biltmore provider and appointment type", {
      providerNpi,
      openAppointmentTypeId,
      internalAppointmentType,
    });
  }

  if (!TPR_BILTMORE_ANY_OPEN_APPOINTMENT_TYPE_IDS.has(openAppointmentTypeIdNumber)) {
    return openAppointmentTypeIdNumber;
  }

  const pairedAppointmentTypeId = eligibleOpenAppointmentTypeIds.find(
    (appointmentTypeId) => !TPR_BILTMORE_ANY_OPEN_APPOINTMENT_TYPE_IDS.has(appointmentTypeId)
  );

  if (pairedAppointmentTypeId === undefined) {
    logAndThrow("No specific TPR Biltmore appointment type is paired with generic open appointment type", {
      providerNpi,
      openAppointmentTypeId,
      internalAppointmentType,
    });
  }

  return pairedAppointmentTypeId;
}

export const tprAthenaConfig = defineAthenaTenantConfig({
  subdomain: ConfigSubdomain.TPR,
  core: {
    practiceId: 600,
    departments: {
      [TPR_BILTMORE_DEPARTMENT_ID]: { name: "Biltmore Cardiology - Camelback", assignee: null },
    },
    patientPrimaryDepartment: PatientPrimaryDepartment.RECEIVING_LOCATION,
    defaultReceivingLocationEhrId: TPR_BILTMORE_DEPARTMENT_ID,
  },
  patient: {
    consent: {
      consenttocall: true,
      consenttotext: true,
      isprivacynoticeonfile: true,
      medicationhistoryconsentverified: null,
    },
    updateInsurance: false,
    referralSource: () => ({
      referralsourceid: TPR_REFERRAL_SOURCE_ID,
      referralsourceother: "",
    }),
    referringProviderMatchStrategy: AthenaReferringProviderMatchStrategy.NPI_FIRST_IGNORE_ZIP,
  },
  insurance: {
    coverageWrite: tprAthenaCoverageWrite,
    patientCoverageDepartmentId: TPR_BILTMORE_DEPARTMENT_ID,
    resolveCasePolicy: (_memberId, coverage) => resolveTprCasePolicyPackageId(coverage),
  },
  documents: {
    referralDocumentTypeId,
  },
  scheduling: {
    suppressAutomaticPatientCasesForVoice: true,
    additionalAppointmentConfirmationDepartmentIds: TPR_ADDITIONAL_APPOINTMENT_CONFIRMATION_DEPARTMENT_IDS,
    requiresReplacementForRescheduling: (source, target) => {
      const allowedTypeIds = getTprBiltmoreOpenAppointmentTypeIds(
        { subdomain: ConfigSubdomain.TPR },
        target.practitioner.npiNumber,
        target.appointmentType
      );

      const sourceTypeId = Number(source.ehrAppointmentTypeId);

      if (allowedTypeIds?.includes(sourceTypeId)) return false;
      if (TPR_BILTMORE_BOOKED_APPOINTMENT_TYPES[sourceTypeId] !== source.type) {
        logAndThrow("Source appointment type is not configured for TPR replacement rescheduling");
      }

      return true;
    },
    sendVoiceCancellationReason: true,
    eligibilityHistoryDepartmentIds: getTprDepartmentIds(),
    additionalAppointmentHistoryDepartmentIds: TPR_RELATED_APPOINTMENT_HISTORY_DEPARTMENT_IDS,
    bookedAppointmentType: getTprBiltmoreAppointmentTypeFromBookedEhrId,
    resolveAppointmentTypeId: ({ group, appointmentType }) => {
      const appointmentTypeId = getTprBiltmoreAppointmentTypeId(group, appointmentType);

      if (appointmentTypeId === null) {
        logAndThrow("No TPR Biltmore appointment type configured", { appointmentType });
      }

      return appointmentTypeId;
    },
  },
});
